import { createContext, useContext, useId, type ComponentProps, type ReactNode } from 'react';
import { cx } from './cx';

const CardTitleId = createContext<string | undefined>(undefined);

export interface CardProps extends ComponentProps<'section'> {
  /** primary = Ace .widget-color-blue2 (blue header), for the main panel of a page. */
  tone?: 'default' | 'primary';
}

/**
 * Ace .widget-box: bordered panel with a header strip. Rendered as a <section> labelled by its
 * CardHeader title, so it shows up as a landmark-like region in screen-reader navigation.
 */
export function Card({ tone = 'default', className, children, ...props }: CardProps) {
  const id = `card${useId().replace(/:/g, '')}`;
  return (
    <CardTitleId.Provider value={id}>
      <section
        aria-labelledby={id}
        data-tone={tone}
        className={cx(
          'group/card flex min-w-0 flex-col border bg-surface',
          tone === 'primary' ? 'border-sidebar-accent' : 'border-border-widget',
          className,
        )}
        {...props}
      >
        {children}
      </section>
    </CardTitleId.Provider>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  icon?: ReactNode;
  /** Buttons/links on the right (Ace .widget-toolbar). */
  actions?: ReactNode;
  /** Heading level so the page outline stays correct (default h2). */
  level?: 2 | 3 | 4;
  className?: string;
}

export function CardHeader({ title, icon, actions, level = 2, className }: CardHeaderProps) {
  const id = useContext(CardTitleId);
  const H = `h${level}` as const;
  return (
    <div
      className={cx(
        'flex min-h-widget-header flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5',
        'border-border-widget bg-surface-raised text-primary-ink',
        'group-data-[tone=primary]/card:border-sidebar-accent group-data-[tone=primary]/card:bg-primary group-data-[tone=primary]/card:text-ink-inverse',
        className,
      )}
    >
      <H id={id} className="flex flex-1 items-center gap-2 text-base font-normal">
        {icon && (
          <span aria-hidden="true" className="inline-flex size-4">
            {icon}
          </span>
        )}
        {title}
      </H>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cx('min-w-0 p-3 md:p-4', className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cx(
        'flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-muted px-3 py-2',
        className,
      )}
      {...props}
    />
  );
}
